"use client";

import { Button } from "@/components/ui/button";
import { ShieldAlert, X } from "lucide-react";
import { useEndImpersonation } from "@/components/admin/use-end-impersonation";
import type { ImpersonationState } from "@/lib/auth/user";

/**
 * The impersonation state arrives as a prop from AuthenticatedContent, which
 * already parses the cookie server-side (AIR-896) — the banner used to POST a
 * getImpersonationStatus server action on every page mount for ~100% of
 * users just to read that cookie.
 */
export function ImpersonationBanner({ state }: { state: ImpersonationState | null }) {
  const { exitImpersonation, isPending } = useEndImpersonation();

  if (!state?.isImpersonating) {
    return null;
  }

  return (
    <div className="fixed top-0 left-0 right-0 z-50 bg-yellow-500 dark:bg-yellow-600 text-black dark:text-white px-4 py-2">
      <div className="container mx-auto flex items-center justify-between">
        <div className="flex items-center gap-3">
          <ShieldAlert className="h-5 w-5" />
          <span className="text-sm font-medium">
            You are impersonating{" "}
            <strong>
              {state.impersonatedUserName || state.impersonatedUserEmail}
            </strong>
            {state.impersonatedUserEmail && state.impersonatedUserName && (
              <span className="ml-1 opacity-80">
                ({state.impersonatedUserEmail})
              </span>
            )}
          </span>
        </div>
        <Button
          variant="outline"
          size="sm"
          className="h-8 bg-white text-black hover:bg-gray-100 dark:bg-gray-800 dark:text-white dark:hover:bg-gray-700"
          onClick={exitImpersonation}
          disabled={isPending}
        >
          <X className="h-4 w-4 mr-1" />
          Exit Impersonation
        </Button>
      </div>
    </div>
  );
}
